import React, { useState, useEffect, useCallback } from 'react';
import { Typography } from '@mui/material';

import { DataWithPagination } from 'services/api';
import {
  Costing,
  getCostingByItemId,
  getCostingByItemIdV2,
} from 'services/inventory';
import { Pagination } from 'services/search';
import { ItemsTable } from 'ui/components/Table/ItemsTable';

import { CONSTING_TABLE_COLUMNS, initialPagination } from './consts';
import { InventoryCostingTabProps } from './types';
import { CostingTableRow } from './components/CostingTableRow';
import { logErrorCtx } from 'app/logging';
import FBOTitleBar from 'ui/theme/components/FBOTitleBar/FBOTitleBar';
import { useFlags } from 'helpers/useFlags';

const InventoryCostingTab: React.FC<InventoryCostingTabProps> = ({
  activeItem,
}) => {
  const flags = useFlags();
  const [costingData, setCostingData] = useState<DataWithPagination<Costing>>({
    data: [],
    pagination: initialPagination,
  });
  const [isLoading, setIsLoading] = useState(false);

  const title = (
    <>
      Costing Layers{' '}
      <Typography color="textSecondary" display="inline">
        FIFO/LIFO
      </Typography>
    </>
  );

  const getCostingData = async (pagination: Pagination) => {
    setIsLoading(true);
    const expands = ['item.defaultUom'];
    try {
      let res;
      if (flags.useCostingLayerV2Endpoint) {
        res = await getCostingByItemIdV2(activeItem.id!, false, {
          expands,
          pagination,
        });
      } else {
        res = await getCostingByItemId(activeItem.id!, {
          expands,
          pagination,
        });
      }
      setCostingData(res);
    } catch (error) {
      logErrorCtx('Error in getCostingData', {
        error: error as Error,
        stackTrace: (error as Error).stack,
        component: 'InventoryCostingTab',
        title: 'Error in getCostingData',
        description: `Costing Item id ${activeItem.id}`,
      });
    }
    setIsLoading(false);
  };

  const handlePaginationChange = useCallback(
    (newPagination: Pagination) => {
      getCostingData(newPagination);
    },
    [flags.useCostingLayerV2Endpoint]
  );

  useEffect(() => {
    getCostingData(initialPagination);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeItem.id, flags.useCostingLayerV2Endpoint]);

  return (
    <>
      <FBOTitleBar title={title}></FBOTitleBar>
      <ItemsTable
        data={costingData.data.filter((item) => !item.voided)} // Todo: filtering to be removed along with FF
        isLoadingData={isLoading}
        columns={CONSTING_TABLE_COLUMNS}
        RenderCustomRow={CostingTableRow}
        selectableItems={false}
        pagination={costingData.pagination}
        onPaginationChange={handlePaginationChange}
        showEnhancedTablePaginationActions
        emptyTableText="When inventory is added the cost information will be displayed here"
      />
    </>
  );
};

export default InventoryCostingTab;
